import { ref } from 'vue'

import type { LocaleCode } from '~/i18n/config'
import type { Salutation } from '~/types/shopware'

export interface ShopSalutationOption {
  value: string
  label: string
}

const READ_SALUTATION_GET = 'readSalutationGet get /salutation' as const
const READ_SALUTATION_POST = 'readSalutation post /salutation' as const

/**
 * Request-scoped salutation loader. Each invocation resolves the language id
 * for the requested locale and sends it as a per-request sw-language-id
 * header, so results never depend on the global default header timing.
 * Exposed options and salutations always belong to optionsLocale; a failed or
 * superseded request never replaces them with another locale's data. The
 * entity list lets existing Salutation[] pass-through props stay type-correct
 * while option mapping stays in one place.
 */
export const useShopSalutations = () => {
  const { apiClient, cacheableReads } = useShopwareContext()
  const { getLanguageByCode } = useShopLanguages()

  const options = ref<ShopSalutationOption[]>([])
  const salutations = ref<Salutation[]>([])
  const optionsLocale = ref<LocaleCode | null>(null)
  const isLoading = ref(false)
  const error = ref<unknown>(null)

  let latestRequest = 0

  const toOptions = (elements: Salutation[]): ShopSalutationOption[] =>
    elements.map((salutation) => ({
      value: salutation.id,
      label: salutation.translated?.displayName || salutation.displayName,
    }))

  const loadSalutations = async (locale: LocaleCode): Promise<void> => {
    const request = ++latestRequest
    isLoading.value = true
    error.value = null
    if (optionsLocale.value !== locale) {
      // Associate immediately: consumers must not render or select a previous
      // locale's list while the new locale is loading or after its failure.
      options.value = []
      salutations.value = []
      optionsLocale.value = locale
    }

    try {
      const language = await getLanguageByCode(locale)
      if (request !== latestRequest) {
        return
      }
      if (!language?.id) {
        throw new Error(`Language not found: ${locale}`)
      }

      const result = cacheableReads
        ? await apiClient.invoke(READ_SALUTATION_GET, { headers: { 'sw-language-id': language.id } })
        : await apiClient.invoke(READ_SALUTATION_POST, {
            headers: { 'sw-language-id': language.id },
            body: {},
          })
      if (request !== latestRequest) {
        return
      }
      const elements = result?.data?.elements ?? []
      options.value = toOptions(elements)
      salutations.value = elements
    } catch (loadError) {
      if (request !== latestRequest) {
        return
      }
      error.value = loadError
      // Options stay as associated at start: an empty list for a new locale,
      // the retained list for a same-locale refresh. Never another locale's.
    } finally {
      if (request === latestRequest) {
        isLoading.value = false
      }
    }
  }

  return {
    options,
    salutations,
    optionsLocale,
    isLoading,
    error,
    loadSalutations,
  }
}
